<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { eorAPI } from '../service/api.js';

// White-labeled to match Diamond Bill Review's "Review Analysis" EOR
// (reference: DBR control #494715, ClickUp 86bcre0wf). Every EOR renders with
// DBR branding. Only the WA bill-notes text and form ID are known so far, so
// they apply to every jurisdiction until other states' wording is supplied.
const FORM_ID = 'DIAMOND_232_WA';
const BILL_NOTES =
  'Amount billed above the recommended allowance are hereby objected to as being in excess of the ' +
  'amount published in the Payment Medical Aid Rules and Fee Schedules, authorized under Washington ' +
  'state law and Administrative code (WAC).';
const DEFAULT_LOGO = '/dbr-logo.png';

const route = useRoute();
const eor = ref(null);
const error = ref('');
const printedAt = ref('');
const currentYear = new Date().getFullYear();
const originalTitle = document.title;
let pageStyle = null;

// "12/9/2025 11:01:39PM" — DBR's generated-timestamp format.
const stamp = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  const h = d.getHours();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ` +
    `${h % 12 || 12}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${h >= 12 ? 'PM' : 'AM'}`;
};

// Quotes a value for use as a CSS `content` string.
const cssString = (s) => '"' + String(s ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\s+/g, ' ') + '"';

// The print footer, page numbers and the page-2+ control number live in @page
// margin boxes, which only accept static strings — so the rule is generated
// from the loaded document. Every margin box is claimed (even with "") because
// Chrome draws its own date/title/URL header into any box the page leaves empty.
const renderPageStyle = () => {
  if (!eor.value) return;
  const control = `Control Number:  ${eor.value.documentControlNumber ?? ''}`;
  const box = "font-family: 'Times New Roman', Times, serif; font-size: 10pt; white-space: pre;";
  const rule = 'border-top: 2px solid #000; padding-top: 4pt; vertical-align: top;';
  if (!pageStyle) {
    pageStyle = document.createElement('style');
    document.head.appendChild(pageStyle);
  }
  // #app's global margin-top/centering (MainView.vue) would otherwise push
  // the whole document down and center it; this sheet only exists while an
  // EOR is mounted, so the override doesn't leak into the rest of the app.
  pageStyle.textContent = `
#app { margin-top: 0; text-align: left; }
@page {
  size: letter;
  margin: 0.35in 0.3in 0.6in;
  @top-left { content: ""; }
  @top-center { content: ""; }
  @top-right { content: ${cssString(control)}; ${box} font-weight: bold; vertical-align: bottom; }
  @bottom-left { content: ${cssString(`${FORM_ID} Copyright © 2000-${currentYear} All rights reserved.`)}; ${box} ${rule} font-weight: bold; white-space: normal; width: 3.05in; }
  @bottom-center { content: ${cssString(printedAt.value)}; ${box} ${rule} width: 1.8in; }
  @bottom-right { content: ${cssString(control + '      ')} "Page " counter(page) " of " counter(pages); ${box} ${rule} font-weight: bold; text-align: right; width: 3.05in; }
}
@page :first { @top-right { content: ""; } }`;
};

const onBeforePrint = () => {
  printedAt.value = stamp(new Date());
  renderPageStyle();
};

onMounted(async () => {
  window.addEventListener('beforeprint', onBeforePrint);
  try {
    const { data } = await eorAPI.getEOR(route.params.id);
    eor.value = data;
    printedAt.value = stamp(new Date());
    document.title = `Review Analysis - ${data.documentControlNumber ?? ''}`;
    renderPageStyle();
  } catch (e) {
    console.error('EOR fetch failed:', e);
    error.value = 'Your session is invalid or has expired. Please request a new access link.';
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeprint', onBeforePrint);
  pageStyle?.remove();
  document.title = originalTitle;
});

const fmt = (cents) => '$' + ((cents ?? 0) / 100).toFixed(2);

// Source dates arrive as YYYY-MM-DD; DBR prints header dates as MM/DD/YYYY and
// dates of service as MM-DD-YYYY. Anything unparseable is shown as-is.
const fmtDate = (s, sep = '/') => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s ?? '');
  return m ? [m[2], m[3], m[1]].join(sep) : (s ?? '');
};

// Falls back to the DBR logo if a customer's stored logo_url is unreachable
// (deleted/renamed GCS object, bad URL) — without this, a bad customerLogoUrl
// renders a broken-image icon on every EOR document that customer's vendors open.
const onLogoError = (event) => {
  if (event.target.src.endsWith(DEFAULT_LOGO)) return;
  event.target.src = DEFAULT_LOGO;
};

// DBR format: "GOMEZ , BERNARDO".
const patientName = computed(() => {
  const { firstName, lastName } = eor.value?.patient ?? {};
  if (!firstName && !lastName) return '';
  return `${lastName ?? ''} , ${firstName ?? ''}`.toUpperCase();
});

const stateJurisdiction = computed(() =>
  [eor.value?.jurisdictionState, eor.value?.payee?.address?.zipCode].filter(Boolean).join(' ')
);

const employerNameId = computed(() => {
  const name = (eor.value?.employerName ?? '').toUpperCase();
  return eor.value?.employerId ? `${name} (${eor.value.employerId})` : name;
});

// "99203-" when there's no modifier — DBR always prints the separator.
const codemod = (item) => [item.billedCode, [item.modifier1, item.modifier2].filter(Boolean).join('-')].join('-');

const allReasonCodes = computed(() => {
  if (!eor.value?.lineItems) return [];
  const seen = new Map();
  for (const item of eor.value.lineItems) {
    for (const rc of item.reasonCodes ?? []) {
      if (!seen.has(rc.code)) seen.set(rc.code, rc.description ?? '');
    }
  }
  return [...seen.entries()].map(([code, description]) => ({ code, description }));
});

const lineReasonCodes = (item) =>
  (item.reasonCodes ?? []).map((rc) => rc.code).join(', ');

const placesOfService = computed(() =>
  [...new Set((eor.value?.lineItems ?? []).map((l) => l.placeOfService).filter(Boolean))].join(', ')
);

const printPage = () => window.print();

const addressLines = (addr) => {
  if (!addr) return [];
  const parts = [addr.line1];
  if (addr.line2) parts.push(addr.line2);
  parts.push(`${[addr.city, addr.state].filter(Boolean).join(', ')} ${addr.zipCode ?? ''}`.trim());
  return parts.filter(Boolean);
};

const providerAddress = computed(() => addressLines(eor.value?.payee?.address));
const insurerAddress = computed(() => addressLines(eor.value?.insurer?.address));
</script>

<template>
  <div class="eor-page">
    <p v-if="error" class="eor-error">{{ error }}</p>
    <p v-else-if="!eor" class="eor-loading">Loading…</p>

    <div v-else class="eor-document">

      <!-- ── Header ─────────────────────────────────────────────── -->
      <div class="eor-header">
        <div class="eor-logo">
          <img :src="eor.customerLogoUrl || DEFAULT_LOGO" alt="Diamond Bill Review" @error="onLogoError" />
        </div>
        <div class="eor-title-block">
          <h1 class="eor-title">Review Analysis</h1>
          <div class="eor-field"><span class="eor-label">Control Number:</span><span class="eor-control">{{ eor.documentControlNumber }}</span></div>
          <div class="eor-field"><span class="eor-label">Date Reviewed:</span><span>{{ fmtDate(eor.dateReviewed) }}</span></div>
          <div class="eor-field"><span class="eor-label">Date RB Client:</span><span>{{ fmtDate(eor.dateReceivedByClient) }}</span></div>
          <div class="eor-field"><span class="eor-label">Reviewed By:</span><span>{{ eor.reviewedBy }}</span></div>
          <div class="eor-field"><span class="eor-label">Adjustor:</span><span>{{ eor.adjusterName }}</span></div>
        </div>
      </div>

      <hr class="eor-rule" />

      <!-- ── Claim info two-column ──────────────────────────────── -->
      <div class="eor-claim-grid">
        <div class="eor-claim-left">
          <div class="eor-field"><span class="eor-label">Claim Number:</span><span>{{ eor.claimNumber }}</span></div>
          <div class="eor-field"><span class="eor-label">Patient Name:</span><span>{{ patientName }}</span></div>
          <div class="eor-field"><span class="eor-label">Provider Tax ID:</span><span>{{ eor.payee?.taxId }}</span></div>
          <div class="eor-field">
            <span class="eor-label">CW#:</span>
            <span>{{ eor.insuredNumber }} <em v-if="eor.ppoId">(PPOID: {{ eor.ppoId }})</em></span>
          </div>
          <div class="eor-field"><span class="eor-label">State Jurisdiction:</span><span>{{ stateJurisdiction }}</span></div>
          <div class="eor-field"><span class="eor-label">PPO Name:</span><span>{{ eor.ppoOrgName }}</span></div>

          <div class="eor-group">
            <div class="eor-field"><span class="eor-label">Employer Policy:</span><span>{{ eor.employerPolicyNumber }}</span></div>
            <div class="eor-field"><span class="eor-label">Employer Name(ID):</span><span>{{ employerNameId }}</span></div>
          </div>

          <div class="eor-group eor-group-wide">
            <div class="eor-field"><span class="eor-label">Pay Code:</span><span>{{ eor.payCode }}</span></div>
            <div class="eor-field"><span class="eor-label">Place of Service:</span><span>{{ placesOfService }}</span></div>
            <div class="eor-field"><span class="eor-label">NPI:</span><span>{{ eor.billingProvider?.npi }}</span></div>
          </div>
        </div>

        <div class="eor-claim-right">
          <div class="eor-field"><span class="eor-label">Date Of Injury:</span><span>{{ fmtDate(eor.dateOfInjury) }}</span></div>
          <div class="eor-field"><span class="eor-label">Patient SS#:</span><span>XXX-XX-XXXX</span></div>
          <div class="eor-field"><span class="eor-label">Patient Account:</span><span>{{ eor.patientAccount }}</span></div>
          <div class="eor-field"><span class="eor-label">Date Received:</span><span>{{ fmtDate(eor.dateReceivedByBrVendor) }}</span></div>
          <div class="eor-field"><span class="eor-label">L&amp;I Claim:</span><span>{{ eor.insuredNumber }}</span></div>
          <div class="eor-field"><span class="eor-label">Provider Invoice:</span><span>{{ eor.providerInvoiceNumber }}</span></div>

          <div class="eor-group">
            <div class="eor-field"><span class="eor-label">Payment Status Code:</span><span>{{ eor.paymentStatusCode }}</span></div>
          </div>

          <div class="eor-block">
            <div class="eor-block-title">Insurer</div>
            <div>TIN: {{ eor.insurer?.taxId }}</div>
            <div>Carrier Name:</div>
            <div class="eor-indent">{{ eor.insurer?.companyName }}</div>
            <div v-for="(line, i) in insurerAddress" :key="i" class="eor-upper">{{ line }}</div>
            <div class="eor-field eor-group"><span class="eor-label eor-plain">Contact Name</span><span>{{ eor.contactName }}</span></div>
            <div class="eor-field"><span class="eor-label eor-plain">Contact Phone Number</span><span>{{ eor.contactPhone }}</span></div>
          </div>

          <div class="eor-block eor-outdent">
            <div class="eor-block-title">Rendering Provider</div>
            <div>NPI:{{ eor.renderingProvider?.npi }}</div>
            <div>Company:{{ eor.renderingProvider?.companyName }}</div>
            <div>First Name:{{ eor.renderingProvider?.firstName }}</div>
            <div>Last Name:{{ eor.renderingProvider?.lastName }}</div>
          </div>
        </div>
      </div>

      <!-- ── Provider ───────────────────────────────────────────── -->
      <div class="eor-block eor-provider">
        <div class="eor-block-title">Provider</div>
        <div v-if="eor.payee?.companyName" class="eor-upper">{{ eor.payee.companyName }}</div>
        <div v-for="(line, i) in providerAddress" :key="i" class="eor-upper">{{ line }}</div>
      </div>

      <!-- ── ICD codes + carrier mailing address ────────────────── -->
      <div class="eor-icd-row">
        <div class="eor-icd">
          <span class="eor-label">ICD Codes:</span>
          <div class="eor-icd-list">
            <div v-for="dx in eor.diagnoses ?? []" :key="dx.code">{{ dx.code }} {{ dx.description }}</div>
          </div>
        </div>
        <div class="eor-mailing">
          <div class="eor-mailing-name">{{ eor.insurer?.companyName }}</div>
          <div v-for="(line, i) in insurerAddress" :key="i" class="eor-upper">{{ line }}</div>
        </div>
      </div>

      <!-- ── Service lines, messages and bill notes ─────────────────
           One table so its header (with the control number in the @page
           top-right box) repeats on every printed page, as DBR's does. -->
      <table class="eor-table">
        <colgroup>
          <col style="width: 7.6%" />
          <col style="width: 11.2%" />
          <col style="width: 4.9%" />
          <col style="width: 28.4%" />
          <col style="width: 7.1%" />
          <col style="width: 6.3%" />
          <col style="width: 6.3%" />
          <col style="width: 6.3%" />
          <col style="width: 9.3%" />
          <col style="width: 12.6%" />
        </colgroup>
        <thead>
          <tr class="eor-head-group">
            <th colspan="5"></th>
            <th colspan="3" class="eor-reduction">REDUCTION</th>
            <th colspan="2"></th>
          </tr>
          <tr class="eor-head-cols">
            <th>Date of<br />Service</th>
            <th>Code-Mod</th>
            <th>Qty</th>
            <th>Service Description</th>
            <th>Provider<br />Charges</th>
            <th>Bill<br />Review</th>
            <th>PPO</th>
            <th>Other</th>
            <th>Recommend<br />Allowance</th>
            <th>Messages</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in eor.lineItems" :key="i" class="eor-line">
            <td class="eor-dos">{{ fmtDate(item.dateOfService, '-') }}</td>
            <td>{{ codemod(item) }}</td>
            <td>{{ item.units }}</td>
            <td class="eor-desc">{{ item.billedCodeDescription }}</td>
            <td>{{ fmt(item.billedAmount) }}</td>
            <td>{{ fmt(item.billReviewReduction) }}</td>
            <td>{{ fmt(item.contractReduction) }}</td>
            <td>$0.00</td>
            <td>{{ fmt(item.recommendedAllowance) }}</td>
            <td class="eor-msgcodes">{{ lineReasonCodes(item) }}</td>
          </tr>
          <tr class="eor-totals">
            <td colspan="4"></td>
            <td>{{ fmt(eor.totalBilledAmount) }}</td>
            <td>{{ fmt(eor.totalBillReviewReduction) }}</td>
            <td>{{ fmt(eor.totalContractReduction) }}</td>
            <td>$0.00</td>
            <td>{{ fmt(eor.totalRecommendedAllowance) }}</td>
            <td></td>
          </tr>

          <!-- ── Messages ─────────────────────────────────────── -->
          <tr v-if="allReasonCodes.length" class="eor-keep">
            <td colspan="10" class="eor-section">
              <div class="eor-section-title">Messages</div>
              <div v-for="rc in allReasonCodes" :key="rc.code" class="eor-message-row">
                <span class="eor-msg-code">{{ rc.code }}</span>
                <span>{{ rc.description }}</span>
              </div>
            </td>
          </tr>

          <!-- ── EOR Bill Notes + contact block ───────────────── -->
          <tr class="eor-keep">
            <td colspan="10" class="eor-section eor-notes">
              <div class="eor-section-title">EOR Bill Notes:</div>
              <p>{{ BILL_NOTES }}</p>
              <div class="eor-underscore"></div>
              <div>Questions regarding this bill may be sent to:</div>
              <div><a href="mailto:BR@Diamondbillreview.com">BR@Diamondbillreview.com</a></div>
              <div>Diamond Bill Review</div>
              <div>PO Box 5128</div>
              <div>San Ramon, CA 94583</div>
              <div>Phone - <a href="tel:+18335702419">833-570-2419</a></div>
              <div>Fax - 925-498-8265</div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- ── Footer (screen only; print uses the @page margin boxes) ── -->
      <div class="eor-footer no-print">
        <span class="eor-footer-strong">{{ FORM_ID }} Copyright © 2000-{{ currentYear }} All rights reserved.</span>
        <span>{{ printedAt }}</span>
        <span class="eor-footer-strong">Control Number: {{ eor.documentControlNumber }}</span>
      </div>

      <!-- ── Print button ───────────────────────────────────────── -->
      <div class="eor-print-bar no-print">
        <button class="eor-print-btn" @click="printPage">Print</button>
      </div>

    </div>
  </div>
</template>

<style scoped>
.eor-page {
  font-family: 'Times New Roman', Times, serif;
  font-size: 9pt;
  text-align: left;
  color: #000;
  background: #fff;
  padding: 1rem;
}
.eor-document {
  max-width: 7.9in;
  margin: 0 auto;
}
.eor-error { color: #c0392b; padding: 2rem; }
.eor-loading { padding: 2rem; color: #888; }

a { color: inherit; text-decoration: none; }

/* Shared label/value rows */
.eor-field { display: flex; }
.eor-label {
  font-weight: bold;
  flex: 0 0 1.3in;
  white-space: nowrap;
}
.eor-label.eor-plain { font-weight: normal; }
.eor-upper { text-transform: uppercase; }
.eor-group { margin-top: 9pt; }
.eor-group-wide { margin-top: 27pt; }

/* Header */
.eor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.eor-logo img { display: block; width: 1.65in; height: auto; }
.eor-title-block { width: 2.6in; }
.eor-title {
  font-size: 19pt;
  font-weight: bold;
  margin: 0.1in 0 4pt 0;
}
.eor-control { font-weight: bold; font-size: 10.5pt; }

.eor-rule { border: none; border-top: 2px solid #000; margin: 6pt 0; }

/* Claim grid */
.eor-claim-grid {
  display: grid;
  grid-template-columns: 1fr 2.6in;
}

.eor-block { margin-top: 18pt; }
.eor-block-title {
  font-weight: bold;
  text-decoration: underline;
}
.eor-indent { padding-left: 0.3in; }
.eor-outdent { margin-left: -0.17in; margin-top: 4pt; }

.eor-provider { margin: 30pt 0 0 0.45in; }

/* ICD codes + carrier mailing address */
.eor-icd-row {
  display: grid;
  grid-template-columns: 1fr 2.6in;
  margin-top: 40pt;
  padding-bottom: 6pt;
}
.eor-icd { display: flex; }
.eor-icd .eor-label { flex-basis: 1.15in; font-size: 7pt; }
.eor-icd-list { font-size: 7pt; }
.eor-mailing { font-size: 10.5pt; }
.eor-mailing-name { font-weight: bold; }

/* Service lines table */
.eor-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.eor-table th,
.eor-table td {
  padding: 1pt 2pt;
  text-align: left;
  vertical-align: bottom;
  font-weight: normal;
  overflow-wrap: break-word;
}
.eor-table thead { border-top: 2px solid #000; }
.eor-head-group th { padding-top: 6pt; }
.eor-table th.eor-reduction {
  text-align: center;
  font-weight: bold;
  border-bottom: 2px solid #000;
}
.eor-head-cols th { padding-top: 3pt; border-bottom: 2px solid #000; }
.eor-line { break-inside: avoid; }
.eor-line td { vertical-align: top; font-size: 7.5pt; }
.eor-line .eor-dos { font-weight: bold; white-space: nowrap; }
.eor-line .eor-desc { font-size: 6.5pt; text-transform: uppercase; }
.eor-line .eor-msgcodes { text-align: center; }
.eor-totals td { font-weight: bold; font-size: 7.5pt; padding-top: 4pt; border-bottom: 2px solid #000; }
.eor-totals td:nth-child(n + 2):nth-child(-n + 6) { border-top: 2px solid #000; }

/* Messages + bill notes rows (full-width cells inside the table) */
.eor-table td.eor-section { padding: 0; vertical-align: top; }
.eor-keep { break-inside: avoid; }
.eor-section-title { font-weight: bold; }
.eor-message-row { display: flex; font-size: 7.5pt; margin-bottom: 2pt; }
.eor-msg-code { flex: 0 0 0.75in; }
.eor-table td.eor-notes { padding-top: 10pt; }
.eor-notes p { margin: 8pt 0 3pt 0; text-align: justify; }
.eor-underscore { border-bottom: 1px solid #000; width: 75%; margin-bottom: 4pt; }

/* Screen footer */
.eor-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 4pt;
  border-top: 2px solid #000;
  font-size: 10pt;
}
.eor-footer-strong { font-weight: bold; }

/* Print button */
.eor-print-bar { margin-top: 1rem; }
.eor-print-btn {
  padding: 0.4rem 1.2rem;
  background: #2c6db5;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
}
.eor-print-btn:hover { background: #1e5099; }

/* Print stylesheet — page size, footer and page numbers come from the
   @page rule generated in renderPageStyle(). */
@media print {
  .no-print { display: none !important; }
  .eor-page { padding: 0; }
  .eor-document { max-width: 100%; }
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
